import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import type { CatalogTenant } from "@/types/tenant-catalog";

const MASTER_TENANT_ID = "00000000-0000-0000-0000-000000000000";
const MASTER_TENANT_SLUG = "master";

interface AdminAccessGroupFieldsProps {
  usersSearch: string;
  tenantSearch: string;
  tenants: CatalogTenant[];
  selectedTenantIds: string[];
  isTenantsLoading: boolean;
  onUsersSearchChange: (value: string) => void;
  onTenantSearchChange: (value: string) => void;
  onToggleTenant: (tenantId: string) => void;
  disabled?: boolean;
}

export const AdminAccessGroupFields: React.FC<AdminAccessGroupFieldsProps> = ({
  usersSearch,
  tenantSearch,
  tenants,
  selectedTenantIds,
  isTenantsLoading,
  onUsersSearchChange,
  onTenantSearchChange,
  onToggleTenant,
  disabled = false,
}) => {
  const { t } = useTranslation();

  const filteredTenants = useMemo(() => {
    const search = tenantSearch.trim().toLowerCase();
    const selectableTenants = tenants.filter(
      (tenant) =>
        tenant.id !== MASTER_TENANT_ID &&
        tenant.slug.toLowerCase() !== MASTER_TENANT_SLUG,
    );

    if (!search) {
      return selectableTenants;
    }

    return selectableTenants.filter((tenant) => {
      const name = tenant.name.toLowerCase();
      const slug = tenant.slug.toLowerCase();
      return name.includes(search) || slug.includes(search);
    });
  }, [tenantSearch, tenants]);

  return (
    <>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Input
          value={usersSearch}
          onChange={(event) => onUsersSearchChange(event.target.value)}
          icon={<Search className="text-muted-foreground h-4 w-4" />}
          placeholder={t("users.groups.create.searchPlaceholder", "Search")}
          label={t(
            "users.groups.create.guesteriaAdminsLabel",
            "Guesteria Admins",
          )}
          disabled={disabled}
        />
        <Input
          value={tenantSearch}
          onChange={(event) => onTenantSearchChange(event.target.value)}
          icon={<Search className="text-muted-foreground h-4 w-4" />}
          placeholder={t("users.groups.create.searchPlaceholder", "Search")}
          label={t("users.groups.create.tenantsLabel", "Tenants")}
          disabled={disabled}
        />
      </div>

      <div className="flex flex-col gap-2">
        <p className="text-foreground text-sm font-medium">
          {t("users.groups.create.tenantsLabel", "Tenants")}
        </p>
        <div className="border-border max-h-[180px] overflow-y-auto rounded-md border p-2">
          {isTenantsLoading ? (
            <p className="text-muted-foreground px-2 py-1 text-sm">
              {t("common.loading", "Loading...")}
            </p>
          ) : null}
          {!isTenantsLoading && filteredTenants.length === 0 ? (
            <p className="text-muted-foreground px-2 py-1 text-sm">
              {t("users.groups.create.noTenantsFound", "No tenants found")}
            </p>
          ) : null}
          {filteredTenants.map((tenant) => {
            const checkboxId = `tenant-${tenant.id}`;

            return (
              <div
                key={tenant.id}
                className="hover:bg-muted flex items-center gap-2 rounded-sm px-2 py-1.5"
              >
                <Checkbox
                  id={checkboxId}
                  checked={selectedTenantIds.includes(tenant.id)}
                  onCheckedChange={() => onToggleTenant(tenant.id)}
                  disabled={disabled}
                />
                <label
                  htmlFor={checkboxId}
                  className="flex min-w-0 flex-1 cursor-pointer items-center gap-2"
                >
                  <span className="text-foreground truncate text-sm">
                    {tenant.name}
                  </span>
                  <span className="text-muted-foreground ml-auto shrink-0 text-xs">
                    {tenant.slug}
                  </span>
                </label>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};
